/**
 * ChatPage.tsx
 *
 * Internal staff chat — two-panel interface.
 * Left: user list with unread counts. Right: chat conversation.
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { MessageSquare, Search, Send, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useIsBelow } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import { scrollToBottomOf } from '@/v5/lib/scrollWithin';
import { useAuthStore } from '@/store/authStore';
import {
  getChatUsers,
  getMessages,
  sendMessage,
  markMessageRead,
} from '@/services/communications.service';
import type { ChatUser, ChatMessage } from '@/services/communications.service';

const POLL_MS = 5_000;

function formatRelativeTime(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function UserListPanel({ users, selectedId, onSelect, loading, searchTerm, onSearchChange, singleColumn }: {
  users: ChatUser[]; selectedId: number | null; onSelect: (id: number) => void;
  loading: boolean; searchTerm: string; onSearchChange: (v: string) => void; singleColumn: boolean;
}) {
  return (
    <div className={singleColumn
      ? 'w-full min-w-0 flex-1 flex flex-col bg-white'
      : 'w-80 flex-shrink-0 border-r border-slate-200 flex flex-col bg-white'}>
      <div className="p-3 border-b border-slate-200">
        <Input placeholder="Search users..." value={searchTerm} onChange={e => onSearchChange(e.target.value)} leftAddon={<Search className="h-4 w-4" />} fullWidth />
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <p className="text-center text-sm text-slate-400 py-8">Loading...</p>
        ) : users.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-8">{searchTerm ? 'No matching users.' : 'No users found.'}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {users.map(user => (
              <li key={user.userId}>
                <button type="button" onClick={() => onSelect(user.userId)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 touch-target-h ${user.userId === selectedId ? 'bg-primary/10 border-r-2 border-primary' : ''}`}>
                  <span className="flex-shrink-0 h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
                    {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{user.firstName} {user.lastName}</p>
                    {user.role && <p className="text-xs text-slate-400 truncate">{user.role}</p>}
                  </div>
                  {user.unreadCount > 0 && <Badge variant="info" size="sm">{user.unreadCount}</Badge>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Bubble({ msg, currentUserId }: { msg: ChatMessage; currentUserId: number }) {
  const isOwn = msg.fromUserId === currentUserId;
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[70%] max-md:max-w-[88%] flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        {!isOwn && <p className="text-xs text-slate-400 mb-1 ml-1">{msg.fromFirstName} {msg.fromLastName}</p>}
        <div className={`px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${isOwn ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-900 rounded-bl-sm'}`}>
          {msg.message}
        </div>
        <p className="text-xs text-slate-400 mt-1 mx-1">{formatRelativeTime(msg.sentAt)}</p>
      </div>
    </div>
  );
}

function ConversationPanel({ selectedUser, currentUserId }: { selectedUser: ChatUser | null; currentUserId: number }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const loadMessages = useCallback(async () => {
    if (!selectedUser) return;
    const result = await getMessages(1);
    const thread = result.data.filter(m =>
      (m.fromUserId === selectedUser.userId && m.toUserId === currentUserId) ||
      (m.fromUserId === currentUserId && m.toUserId === selectedUser.userId),
    );
    setMessages(thread.slice().reverse());
    for (const m of thread) {
      if (!m.isRead && m.fromUserId === selectedUser.userId) void markMessageRead(m.messageId);
    }
  }, [selectedUser, currentUserId]);

  useEffect(() => {
    if (!selectedUser) { setMessages([]); return; }
    setLoading(true);
    void loadMessages().finally(() => setLoading(false));
    const interval = setInterval(() => { void loadMessages(); }, POLL_MS);
    return () => clearInterval(interval);
  }, [selectedUser, loadMessages]);

  useEffect(() => {
    // Scroll this pane, not every scrollable ancestor. `bottomRef.scrollIntoView()`
    // walks out of the message list and moves whatever else can move — below `md` the
    // shell releases the viewport lock, so the document itself scrolls, and the clipped
    // column row shifts sideways past the user list. That is SCRUM-1362's defect
    // exactly; this page is its second instance and was missed because the fix landed
    // on the Comm Center's thread component rather than on the shared idiom.
    const scroller = scrollerRef.current;
    if (scroller) scrollToBottomOf(scroller, true);
  }, [messages]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || !selectedUser || sending) return;
    setSending(true); setInput('');
    try { await sendMessage(selectedUser.userId, text); void loadMessages(); }
    finally { setSending(false); }
  }

  if (!selectedUser) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-slate-50">
        <MessageSquare className="h-12 w-12 text-slate-300 mb-3" />
        <p className="text-slate-400 text-sm">Select a conversation</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 flex-shrink-0">
        <span className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-600">
          {selectedUser.firstName.charAt(0)}{selectedUser.lastName.charAt(0)}
        </span>
        <div>
          <p className="text-sm font-semibold text-slate-900">{selectedUser.firstName} {selectedUser.lastName}</p>
          {selectedUser.role && <p className="text-xs text-slate-400">{selectedUser.role}</p>}
        </div>
      </div>
      {/* max-lg:pr-9 clears the AI assistant's edge tab, which is `fixed right-0 top-1/2
          ... lg:hidden` and mounted app-wide — so it overlaps own-message bubbles,
          which are right-aligned, from 0 to 1023.98px. Same gutter ConversationThread
          carries; this page was missed. */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-5 py-4 max-md:px-3 max-lg:pr-9">
        {loading ? (
          <p className="text-center text-sm text-slate-400 py-8">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-8">No messages yet. Say hello!</p>
        ) : (
          <>{messages.map(msg => <Bubble key={msg.messageId} msg={msg} currentUserId={currentUserId} />)}</>
        )}
      </div>
      {/* py-3 split into pt-3 + pb-safe-plus-3 — see ConversationThread's composer for
          why the two cannot be layered. env() is 0px on desktop, so it still computes
          the same 0.75rem. */}
      <form onSubmit={handleSend} className="flex items-center gap-3 px-5 pt-3 pb-safe-plus-3 border-t border-slate-200 bg-white flex-shrink-0 max-md:px-3">
        <input type="text" value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..."
          className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" disabled={sending} />
        <Button type="submit" size="sm" disabled={!input.trim() || sending} loading={sending} iconOnly leftIcon={<Send className="h-4 w-4" />} className="rounded-full" aria-label="Send message" />
      </form>
    </div>
  );
}

export default function ChatPage() {
  const navigate = useNavigate();
  const currentUserId = useAuthStore(s => s.user?.id);
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // The layout splits at `lg`, not `md`.
  //
  // `md` is the wrong boundary for a list beside a conversation, by the same arithmetic
  // SCRUM-1362 applied to the Comm Center: at 768 the global nav rail is still in flow
  // at 16rem, so a 320px user list leaves the conversation about 192px. This page was
  // converted in the same batch and kept the `md` split.
  //
  // Only one tier is needed in JS. The viewport re-lock is a `max-md:` class on the
  // root, because it mirrors the shell's own `max-md:` release rather than this page's
  // column count — the two boundaries are independent and only look alike.
  const singleColumn = useIsBelow('lg');

  // The on-screen keyboard does not shrink the layout viewport, so a page sized in
  // `dvh` — or in `100%` of a locked shell — puts its pinned composer underneath it.
  // Enabled on the whole single-column tier, not just the phone: the composer is the
  // bottom-pinned element at every width where the conversation is the screen.
  useVisualViewportVars(singleColumn && selectedUserId != null);

  const loadUsers = useCallback(async () => { const data = await getChatUsers(); setUsers(data); }, []);

  useEffect(() => {
    setLoadingUsers(true);
    void loadUsers().finally(() => setLoadingUsers(false));
    const interval = setInterval(() => { void loadUsers(); }, POLL_MS);
    return () => clearInterval(interval);
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const q = searchTerm.toLowerCase();
    return users.filter(u => u.firstName.toLowerCase().includes(q) || u.lastName.toLowerCase().includes(q));
  }, [users, searchTerm]);

  const selectedUser = users.find(u => u.userId === selectedUserId) ?? null;

  if (!currentUserId) {
    return <div className="p-6 text-center text-sm text-slate-500">Unable to determine current user.</div>;
  }

  // One column at a time on a phone: 375px cannot carry a 320px user list beside a
  // conversation. See MessagesPage for the same treatment of the Comm Center.
  // max-md: re-locks the viewport height Layout.tsx releases below `md`; 3rem is its
  // h-12 md:hidden header.
  const showList = !singleColumn || selectedUserId == null;
  const showChat = !singleColumn || selectedUserId != null;

  return (
    <div className="flex flex-col h-full max-md:h-[calc(var(--vv-height,100dvh)_-_3rem)] md:max-lg:h-[var(--vv-height,100%)]">
      <PageHeader
        title="Staff Chat"
        subtitle="Internal messaging between staff members."
        actions={
          <Button variant="secondary" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/messages')}>
            Back to Messages
          </Button>
        }
      />
      <div className="flex flex-1 min-h-0 rounded-lg border border-slate-200 overflow-hidden">
        {showList && (
          <UserListPanel users={filteredUsers} selectedId={selectedUserId} onSelect={setSelectedUserId}
            loading={loadingUsers} searchTerm={searchTerm} onSearchChange={setSearchTerm} singleColumn={singleColumn} />
        )}
        {showChat && (singleColumn ? (
          // The wrapper exists only to host the back control, so it is not rendered
          // above the phone tier — those widths keep today's exact node tree.
          <div className="flex min-w-0 flex-1 flex-col bg-white">
            <div className="flex shrink-0 items-center border-b border-slate-200 bg-white px-3 py-2">
              <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}
                onClick={() => setSelectedUserId(null)}>
                Back
              </Button>
            </div>
            <ConversationPanel selectedUser={selectedUser} currentUserId={currentUserId} />
          </div>
        ) : (
          <ConversationPanel selectedUser={selectedUser} currentUserId={currentUserId} />
        ))}
      </div>
    </div>
  );
}
